type JSONPrimitive = string | number | boolean | null | undefined;

export type JSONValue = JSONPrimitive | JSONValue[] | {
    [key: string]: JSONValue;
};

type EventPrefix = "zl-"

interface CloseEvent {
	type: `${EventPrefix}close`
}

interface HomeEvent {
	type: `${EventPrefix}home`
}

interface LoadEvent {
	type: `${EventPrefix}load`
	id: string
}

interface LoadEventResponse {
	type: `${EventPrefix}load-done`
	id: string
	success: boolean
	data?: JSONValue
}

interface SaveEvent {
	type: `${EventPrefix}save`
	id?: string
	data: JSONValue
}

interface SaveEventResponse {
	type: `${EventPrefix}save-done`
	id: string
	success: boolean
}

interface EnumerateEvent {
	type: `${EventPrefix}enumerate`
}

interface EnumerateEventResponse {
	type: `${EventPrefix}enumerate-done`
	success: boolean
	items: {
		id: string
		data: JSONValue
	}[]
}

interface RemoveEvent {
	type: `${EventPrefix}remove`
	id: string
}

interface RemoveEventResponse {
	type: `${EventPrefix}remove-done`
	success: boolean
}

interface ResizeEvent {
	type: `${EventPrefix}resize`
	width: number
	height: number
}

type EventData = CloseEvent | HomeEvent | LoadEvent | SaveEvent | EnumerateEvent | RemoveEvent | ResizeEvent
type ResponseTypes = LoadEventResponse | SaveEventResponse | EnumerateEventResponse | RemoveEventResponse

let parentOrigin : string | null = null;
try {
	parentOrigin = window.parent.origin
}
catch {
	console.debug("Failed to get parent origin")
}

const targetOrigin = parentOrigin || "https://slate.auto"

const sendEvent = (ev: EventData) => {
	window.parent?.postMessage(ev, {targetOrigin})
}

const sendEventWithResponse = async <T extends ResponseTypes>(ev: EventData, timeoutMs: number = 10000) : Promise<T> => {
	const requestUUID = crypto.randomUUID()
	const responseType = `${ev.type}-response`
	let listener = undefined;
	let timeoutHandle : ReturnType<typeof setTimeout> | undefined = undefined;
	const timeout = new Promise<T>((_, r) => {
		const onTimeout = () => {
			//console.debug(`Timed out after waiting ${timeoutMs}ms for response to ${ev.type} of ${requestUUID}`)
			r()
		}
		timeoutHandle = setTimeout(onTimeout, timeoutMs)
	})
	const action = new Promise<ResponseTypes>((resolve, reject) => {
		listener = (ev: MessageEvent) => {
			if(ev.data.type === responseType && ev.data.requestUUID === requestUUID) {
				const data = ev.data as ResponseTypes
				if(timeoutHandle) {
					clearTimeout(timeoutHandle)
				}
				if(data.success) {
					resolve(data)
				}
				else {
					reject()
				}
			}
		}
		window.addEventListener("message", listener)
		//console.debug(`Adding listener for ${ev.type}-response of ${requestUUID}`)
		window.parent?.postMessage({...ev, requestUUID}, {targetOrigin})
		//console.debug(`Sent ${ev.type} as ${requestUUID}`)
	})

	try {
		const result = await Promise.race([
			action,
			timeout,
		])
		return result as T
	}
	finally {
		if(listener) {
			window.removeEventListener("message", listener)
		}
	}
}

const SaveDataPrefix = "zl-savedata-"

export const save = async (data: JSONValue, id: string) : Promise<string> => {
	if(window.parent && window.parent !== window) {
		const result = await sendEventWithResponse<SaveEventResponse>({type: "zl-save", id, data})
		return result.id
	}
	else {
		if(window.localStorage) {
			id = id || crypto.randomUUID()
			const saveData = JSON.stringify(data)
			window.localStorage.setItem(`${SaveDataPrefix}${id}`, saveData)
			return id
		}
		throw Error("No localStorage")
	}
}

export const load = async (id: string) : Promise<JSONValue> => {
	if(window.parent && window.parent !== window) {
		const result = await sendEventWithResponse<LoadEventResponse>({type: "zl-load", id})
		return result.data
	}
	else {
		if(window.localStorage) {
			const saveData = window.localStorage.getItem(`${SaveDataPrefix}${id}`)
			if(saveData !== null) {
				return JSON.parse(saveData)
			}
			throw Error(`No save data found for ${id}`)
		}
		throw Error("No localStorage")
	}
}

export const enumerate = async () : Promise<{id: string, data: JSONValue}[]> => {
	if(window.parent && window.parent !== window) {
		const result = await sendEventWithResponse<EnumerateEventResponse>({type: "zl-enumerate"})
		return result.items
	}
	else {
		if(window.localStorage) {
			const items : {id: string, data: JSONValue}[] = []
			for(let index = 0; index < localStorage.length; ++index) {
				const key = localStorage.key(index);
				if(key?.startsWith(SaveDataPrefix)) {
					items.push({
						id: key.substring(SaveDataPrefix.length),
						data: JSON.parse(localStorage.getItem(key) ?? "")
					})
				}
			}
			return items
		}
		throw Error("No localStorage")
	}
}

export const remove = async (id: string) : Promise<void> => {
	if(window.parent && window.parent !== window) {
		await sendEventWithResponse<RemoveEventResponse>({type: "zl-remove", id})
	}
	else {
		if(window.localStorage) {
			const saveData = window.localStorage.getItem(`${SaveDataPrefix}${id}`)
			if(saveData !== null) {
				window.localStorage.removeItem(`${SaveDataPrefix}${id}`)
				return
			}
			throw Error(`No save data found for ${id}`)
		}
		throw Error("No localStorage")
	}
}

export const close = () => {
	if(window.parent && window.parent !== window) {
		sendEvent({type: "zl-close"})
	}
	else {
		window.location.href = "https://slate.auto"
	}
}

export const homepage = () => {
	if(window.parent && window.parent !== window) {
		sendEvent({type: "zl-home"})
	}
	else {
		window.location.href = "https://slate.auto"
	}
}

export const resize = (width: number, height: number) => {
	sendEvent({type: "zl-resize", width, height})
}